import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

/** What GET /bulk-actions/running answers, when a run is in flight. */
interface RunningRun {
  id: string;
  number: number | null;
  oltName: string | null;
  action: string;
  total: number;
  ok: number;
  failed: number;
  left: number;
  pct: number;
  stopRequested: boolean;
}

/**
 * The run in flight, visible from EVERY page.
 *
 * A 40-minute fleet operation used to be watchable only from the bulk page,
 * and only in the tab that started it — leave, and the bar was gone while 190
 * subscribers were still being written to. This asks one cheap question every
 * fifteen seconds and, whenever the answer is a run, pins it under the header
 * everywhere: run number, chassis, the bar, and the counts. Clicking it lands
 * on the bulk page, where Stop and the full per-ONU ledger live.
 *
 * Renders nothing at all when nothing runs — no reserved space, no empty
 * chrome.
 */
export function ActiveBulkRun() {
  const query = useQuery({
    queryKey: ['bulk-running'],
    queryFn: () => api.get<{ running: RunningRun | null }>('/bulk-actions/running'),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const run = query.data?.running;
  if (!run) return null;

  return (
    <Link
      // /bulk is not a route — it fell through to the dashboard.
      to="/bulk-actions"
      className="block border-b border-line bg-primary-soft px-4 py-2 transition-colors hover:brightness-105"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 text-sm">
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
        <span className="font-medium text-ink">
          Run #{run.number ?? '—'} · {run.action.replace(/_/g, ' ')} on {run.oltName}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block h-2 overflow-hidden rounded-full bg-surface-2">
            <span
              className="block h-full rounded-full bg-primary transition-all"
              style={{ width: `${run.pct}%` }}
            />
          </span>
        </span>
        <span className="shrink-0 tabular-nums text-muted">
          {run.pct}% · {run.ok} ok
          {run.failed > 0 ? ` · ${run.failed} failed` : ''} · {run.left} left
        </span>
        {run.stopRequested && (
          <span className="shrink-0 text-xs font-medium text-warn">stopping…</span>
        )}
      </div>
    </Link>
  );
}
